(() => {
  const STORAGE_KEY = 'mps-wishlist-handles-v1';
  const LEGACY_STORAGE_KEY = 'user_wishlist';
  const MAX_ITEMS = 60;
  const OPEN_CLASS = 'mps-wishlist-drawer-open';
  const DRAWER_SELECTOR = '[data-mps-wishlist-drawer]';
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  if (window.MPSWishlist) return;

  let handles = readHandles();
  let opener = null;
  let closeTimer = null;
  let renderToken = 0;
  const productCache = new Map();

  function parseStorage(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || '[]');
    } catch (_error) {
      return [];
    }
  }

  function hasStorageKey(key) {
    try {
      return window.localStorage.getItem(key) !== null;
    } catch (_error) {
      return false;
    }
  }

  function normaliseHandles(value) {
    if (!Array.isArray(value)) return [];

    const seen = new Set();
    return value
      .map((handle) => String(handle || '').trim())
      .filter((handle) => handle.length > 0 && handle.length < 150 && !handle.includes('/'))
      .filter((handle) => {
        if (seen.has(handle)) return false;
        seen.add(handle);
        return true;
      })
      .slice(0, MAX_ITEMS);
  }

  function readHandles() {
    const current = normaliseHandles(parseStorage(STORAGE_KEY));
    if (current.length || hasStorageKey(STORAGE_KEY)) return current;

    const legacy = normaliseHandles(parseStorage(LEGACY_STORAGE_KEY));
    if (legacy.length) {
      persistHandles(legacy);
      return legacy;
    }

    return [];
  }

  function persistHandles(nextHandles) {
    const cleanHandles = normaliseHandles(nextHandles);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanHandles));
    } catch (_error) {
      // Keep the in-memory list working when storage is blocked.
    }

    return cleanHandles;
  }

  function saveHandles(nextHandles) {
    handles = persistHandles(nextHandles);
    syncControls();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[character]);
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency
      }).format(amount);
    } catch (_error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function getStatusElement() {
    const drawer = getDrawer();
    return drawer ? drawer.querySelector('[data-mps-wishlist-status]') : null;
  }

  function setStatus(message) {
    const statusElement = getStatusElement();
    if (statusElement) statusElement.textContent = message;
  }

  function isDrawerOpen() {
    const drawer = getDrawer();
    return Boolean(drawer && !drawer.hidden);
  }

  function productHref(product) {
    return product.url || `/products/${encodeURIComponent(product.handle)}`;
  }

  function imageSrc(product) {
    const featuredImage = product.featured_image;
    let src = '';

    if (typeof featuredImage === 'string') {
      src = featuredImage;
    } else if (featuredImage && typeof featuredImage === 'object') {
      src = featuredImage.src || featuredImage.url || '';
    }

    if (!src && Array.isArray(product.images) && product.images.length) src = product.images[0];
    if (!src) return '';
    if (src.startsWith('//')) src = `${window.location.protocol}${src}`;

    if (src.includes('cdn.shopify.com') || src.includes('shopifycdn.net')) {
      return src.includes('?') ? `${src}&width=184` : `${src}?width=184`;
    }

    return src;
  }

  function priceText(product) {
    const price = product.price_min || product.price || 0;
    return product.price_varies ? `From ${formatMoney(price)}` : formatMoney(price);
  }

  function syncControls() {
    const count = handles.length;

    document.querySelectorAll('[data-mps-wishlist-count]').forEach((element) => {
      element.textContent = count > 99 ? '99+' : String(count);
      element.hidden = count === 0;
      element.setAttribute('aria-label', `${count} saved ${count === 1 ? 'item' : 'items'}`);
    });

    document.querySelectorAll('[data-mps-wishlist-toggle]').forEach((button) => {
      const handle = button.dataset.productHandle;
      const title = button.dataset.productTitle || 'this product';
      const active = Boolean(handle && handles.includes(handle));
      const label = button.querySelector('[data-mps-wishlist-label]');

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute(
        'aria-label',
        active ? `Remove ${title} from wishlist` : `Save ${title} for later`
      );

      if (label) label.textContent = active ? 'Saved for later' : 'Save for later';
    });
  }

  function toggleHandle(handle) {
    if (!handle) return;

    const isSaved = handles.includes(handle);
    const nextHandles = isSaved
      ? handles.filter((savedHandle) => savedHandle !== handle)
      : [handle, ...handles];

    saveHandles(nextHandles);
    setStatus(isSaved ? 'Removed from wishlist.' : 'Saved for later.');
    if (isDrawerOpen()) renderDrawer();
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (response.ok) return response.json();
        // Only a 404 means the product is really gone; other failures are temporary.
        return response.status === 404 ? { mpsMissing: true, handle } : null;
      })
      .catch(() => null);

    productCache.set(handle, request);

    const product = await request;
    if (!product || product.mpsMissing) productCache.delete(handle);
    return product;
  }

  function emptyMarkup() {
    return `
      <div class="mps-wishlist-drawer__empty">
        <p>No products saved yet.</p>
        <a class="btn btn--secondary" href="/collections/all">Browse parts</a>
      </div>
    `;
  }

  function loadingMarkup() {
    return '<div class="mps-wishlist-drawer__loading">Loading saved products...</div>';
  }

  function productMarkup(product) {
    const url = escapeHtml(productHref(product));
    const title = escapeHtml(product.title);
    const vendor = escapeHtml(product.vendor || '');
    const handle = escapeHtml(product.handle);
    const image = imageSrc(product);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariant = variants.find((variant) => variant.available);
    const canDirectAdd = Boolean(availableVariant && variants.length === 1 && !product.requires_selling_plan);
    const imageMarkup = image
      ? `<img src="${escapeHtml(image)}" alt="${title}" loading="lazy" decoding="async">`
      : '<span class="mps-wishlist-item__image-placeholder" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="icon icon--heart"><path d="M20.2 4.8c-2.1-2-5.4-1.9-7.4.2l-.8.9-.8-.9C9.2 2.9 5.9 2.8 3.8 4.8c-2.2 2.1-2.3 5.7-.2 7.9L12 21l8.4-8.3c2.1-2.2 2-5.8-.2-7.9Z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
    const actionMarkup = canDirectAdd
      ? `<button type="button" class="mps-wishlist-item__add" data-mps-wishlist-add="${availableVariant.id}">Add to cart</button>`
      : `<a class="mps-wishlist-item__view" href="${url}">Choose options</a>`;

    return `
      <article class="mps-wishlist-item" data-mps-wishlist-item="${handle}">
        <a class="mps-wishlist-item__image" href="${url}" aria-label="View ${title}">
          ${imageMarkup}
        </a>
        <div class="mps-wishlist-item__body">
          <a class="mps-wishlist-item__title" href="${url}">${title}</a>
          ${vendor ? `<span class="mps-wishlist-item__vendor">${vendor}</span>` : ''}
          <span class="mps-wishlist-item__price">${escapeHtml(priceText(product))}</span>
          <div class="mps-wishlist-item__actions">
            ${actionMarkup}
            <button type="button" class="mps-wishlist-item__remove" data-mps-wishlist-remove="${handle}">Remove</button>
          </div>
        </div>
      </article>
    `;
  }

  async function renderDrawer() {
    const drawer = getDrawer();
    const content = drawer ? drawer.querySelector('[data-mps-wishlist-content]') : null;
    if (!content) return;

    const currentToken = ++renderToken;
    const currentHandles = [...handles];

    if (!currentHandles.length) {
      content.removeAttribute('aria-busy');
      content.innerHTML = emptyMarkup();
      return;
    }

    content.setAttribute('aria-busy', 'true');
    content.innerHTML = loadingMarkup();

    const results = await Promise.all(currentHandles.map(fetchProduct));
    if (currentToken !== renderToken) return;

    const products = results.filter((product) => product && !product.mpsMissing);
    const missingHandles = results
      .filter((product) => product && product.mpsMissing)
      .map((product) => product.handle);
    if (missingHandles.length) {
      saveHandles(handles.filter((handle) => !missingHandles.includes(handle)));
    }

    content.removeAttribute('aria-busy');
    content.innerHTML = products.length
      ? `<div class="mps-wishlist-list">${products.map(productMarkup).join('')}</div>`
      : emptyMarkup();
  }

  function visibleFocusableElements(drawer) {
    return Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (element.closest('[hidden]')) return false;
      return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    });
  }

  function handleKeydown(event) {
    const drawer = getDrawer();
    if (!drawer || drawer.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = visibleFocusableElements(drawer);
    if (!focusableElements.length) {
      event.preventDefault();
      const panel = drawer.querySelector('.mps-wishlist-drawer__panel');
      if (panel) panel.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function openDrawer(trigger) {
    const drawer = getDrawer();
    if (!drawer) return;

    opener = trigger || document.activeElement;
    clearTimeout(closeTimer);
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(OPEN_CLASS);
    document.addEventListener('keydown', handleKeydown);

    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      const closeBtn = drawer.querySelector('[data-mps-wishlist-close]');
      if (closeBtn) closeBtn.focus();
    });

    renderDrawer();
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer || drawer.hidden) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(OPEN_CLASS);
    document.removeEventListener('keydown', handleKeydown);

    closeTimer = window.setTimeout(() => {
      drawer.hidden = true;
    }, 220);

    if (opener && typeof opener.focus === 'function') opener.focus();
    opener = null;
  }

  async function addToCart(button) {
    const variantId = Number(button.dataset.mpsWishlistAdd);
    if (!variantId) return;

    const originalText = button.textContent;
    const formData = new FormData();
    const cartDrawer = document.querySelector('cart-drawer');
    const sections = ['cart-icon-bubble'];

    if (cartDrawer && cartDrawer.closest('.shopify-section')) {
      sections.push(cartDrawer.closest('.shopify-section').id.replace('shopify-section-', ''));
    }

    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);

    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch((window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(data.description || data.message || response.status);

      if (data.sections) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections['cart-icon-bubble']) {
          const template = document.createElement('template');
          template.innerHTML = data.sections['cart-icon-bubble'];
          const updatedBubble = template.content.querySelector('.shopify-section');
          cartIconBubble.innerHTML = updatedBubble ? updatedBubble.innerHTML : data.sections['cart-icon-bubble'];
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, false);
        }
      }

      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId }
      }));

      button.textContent = 'Added';
      setStatus('Product added to cart.');

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1400);
    } catch (_error) {
      button.disabled = false;
      button.textContent = 'Try again';
      setStatus('Could not add product to cart. Please choose options on the product page.');

      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1800);
    }
  }

  document.addEventListener('click', (event) => {
    const openerButton = event.target.closest('[data-mps-wishlist-open]');
    if (openerButton) {
      event.preventDefault();
      openDrawer(openerButton);
      return;
    }

    const closeButton = event.target.closest('[data-mps-wishlist-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const toggleButton = event.target.closest('[data-mps-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      toggleHandle(toggleButton.dataset.productHandle);
      return;
    }

    const removeButton = event.target.closest('[data-mps-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      toggleHandle(removeButton.dataset.mpsWishlistRemove);
      return;
    }

    const addButton = event.target.closest('[data-mps-wishlist-add]');
    if (addButton) {
      event.preventDefault();
      addToCart(addButton);
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY && event.key !== LEGACY_STORAGE_KEY) return;
    handles = readHandles();
    syncControls();
    if (isDrawerOpen()) renderDrawer();
  });

  document.addEventListener('shopify:section:load', syncControls);
  document.addEventListener('DOMContentLoaded', syncControls);

  window.MPSWishlist = {
    open: openDrawer,
    close: closeDrawer,
    getHandles: () => [...handles],
    toggle: toggleHandle
  };

  syncControls();
})();
